import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    AlertTitle,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Public as PublicIcon,
    LinkOff as LinkOffIcon,
    OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

import { useAppDispatch } from 'utilities/hooks';
import { getPublicLink, createPublicLink, deletePublicLink } from 'slices/documentsActions';
import { closeModal } from 'slices/modalsSlice';
import { addSnackbar, SnackbarTypes } from 'slices/snackbarsSlice';
import { Document } from 'interfaces/documents';
import BaseModal, { BaseModalRef } from '../BaseModal';

// must be unique
export const modalName = 'publicLinkModal';

// serializable
export interface IPublicLinkModalProps {
    document?: Document;
}

// non serializable
export interface IPublicLinkModalCallbacks {
    onChange?: () => void;
}

interface PublicLink {
    token: string;
    url: string;
}

const PublicLinkModal = ({
    id,
    zIndex,
    props,
    callbacks,
}: {
    id: string;
    zIndex: number;
    props: IPublicLinkModalProps;
    callbacks: IPublicLinkModalCallbacks;
}) => {
    const dispatch = useAppDispatch();
    const modalRef = useRef<BaseModalRef>(null);
    const { document: doc } = props;
    const { onChange } = callbacks || {};

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [working, setWorking] = useState(false);
    const [link, setLink] = useState<PublicLink | null>(null);

    useEffect(() => {
        if (props && !open) {
            setOpen(true);
        }
    }, [props]);

    useEffect(() => {
        if (!open || !doc?.id) return;
        (async () => {
            setLoading(true);
            try {
                const response = await dispatch(getPublicLink(doc.id)).unwrap();
                setLink(response?.data ?? null);
            } catch (error) {
                setLink(null);
            }
            setLoading(false);
        })();
    }, [open, doc?.id]);

    const handleClose = () => {
        setOpen(false);
        dispatch(closeModal({ modal: modalName, id }));
    };

    const handleCreate = async () => {
        if (!doc?.id) return;
        setWorking(true);
        try {
            const response = await dispatch(createPublicLink(doc.id)).unwrap();
            setLink(response?.data ?? null);
            onChange?.();
            dispatch(addSnackbar({
                type: SnackbarTypes.SUCCESS,
                message: 'Public link created',
            }));
        } catch (error: any) {
            dispatch(addSnackbar({
                type: SnackbarTypes.ERROR,
                message: error?.data?.message || 'Failed to create public link',
            }));
        }
        setWorking(false);
    };

    const handleDelete = async () => {
        if (!doc?.id) return;
        setWorking(true);
        try {
            await dispatch(deletePublicLink(doc.id)).unwrap();
            setLink(null);
            onChange?.();
            dispatch(addSnackbar({
                type: SnackbarTypes.SUCCESS,
                message: 'Public link removed',
            }));
        } catch (error: any) {
            dispatch(addSnackbar({
                type: SnackbarTypes.ERROR,
                message: error?.data?.message || 'Failed to remove public link',
            }));
        }
        setWorking(false);
    };

    const handleCopy = async () => {
        if (!link?.url) return;
        try {
            await navigator.clipboard.writeText(link.url);
            dispatch(addSnackbar({
                type: SnackbarTypes.SUCCESS,
                message: 'Link copied to clipboard',
            }));
        } catch (error) {
            dispatch(addSnackbar({
                type: SnackbarTypes.ERROR,
                message: 'Could not copy link',
            }));
        }
    };

    const renderBody = () => {
        if (loading) {
            return (
                <Box textAlign="center" p={4}>
                    <CircularProgress size={28} />
                </Box>
            );
        }

        if (link) {
            return (
                <Box>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <AlertTitle>This file is public</AlertTitle>
                        Anyone with this link can view and download
                        {doc?.name ? ` “${doc.name}”` : ' this file'} without signing in.
                        Remove the link at any time to revoke access.
                    </Alert>

                    <TextField
                        fullWidth
                        label="Public link"
                        value={link.url}
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Open link">
                                        <IconButton
                                            edge="end"
                                            onClick={() => window.open(link.url, '_blank')}
                                        >
                                            <OpenInNewIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Copy link">
                                        <IconButton edge="end" onClick={handleCopy}>
                                            <CopyIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                        onFocus={(e) => e.target.select()}
                    />
                </Box>
            );
        }

        return (
            <Box>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <AlertTitle>Make this file public?</AlertTitle>
                    Creating a public link makes
                    {doc?.name ? ` “${doc.name}”` : ' this file'} accessible to
                    <strong> anyone with the link</strong> — no sign-in required.
                    The link stays active until you remove it.
                </Alert>
                <Typography variant="body2" color="text.secondary">
                    Only share this link with people you trust.
                </Typography>
            </Box>
        );
    };

    return (
        <BaseModal
            ref={modalRef}
            open={open}
            onClose={handleClose}
            showCloseButton
            maxWidth="sm"
            style={{ zIndex }}
            titleComponent={
                <Box display="flex" alignItems="center" gap={1} sx={{ p: 2, px: 3 }}>
                    <PublicIcon color="warning" />
                    <Typography variant="h6">Public link</Typography>
                </Box>
            }
            actions={
                <Box display="flex" gap={1}>
                    <Button onClick={handleClose} disabled={working}>
                        Close
                    </Button>
                    {!loading && link && (
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={working ? <CircularProgress size={16} /> : <LinkOffIcon />}
                            onClick={handleDelete}
                            disabled={working}
                        >
                            Remove link
                        </Button>
                    )}
                    {!loading && !link && (
                        <Button
                            color="warning"
                            variant="contained"
                            startIcon={working ? <CircularProgress size={16} color="inherit" /> : <PublicIcon />}
                            onClick={handleCreate}
                            disabled={working}
                        >
                            Create public link
                        </Button>
                    )}
                </Box>
            }
        >
            {renderBody()}
        </BaseModal>
    );
};

export default PublicLinkModal;
